/**
 * Legacy cache migrator for the Buildings listing.
 *
 * Converts the old Kendo localStorage shape:
 *   { page: { skip, take }, sort: [{ field, dir }], filters: { values, active, operators }, columns: string[] }
 * into a partial ListingPreferences<BuildingFilters> understood by useListingPreferences.
 *
 * Contract rules applied here:
 *  - Accept the { data: ... } envelope (handled by useListingPreferences).
 *  - Convert page.take → pageSize; page.skip is ignored (page number is never restored).
 *  - Convert sort[0] → { field, direction }. Drop sorts on NON_SORTABLE_COLUMN_KEYS.
 *  - Convert filters.active (boolean map) → activeFilterKeys array.
 *  - Convert old multi-select values (empty string / single value / array) → typed number[] arrays.
 *  - Convert old boolean-select string ('true'/'false'/'') → boolean | null.
 *  - Scope-injected values (account in account scope) are stripped — scope is re-applied by the adapter.
 *  - Search is never read or written.
 *  - Invalid / unrecognizable shapes return null.
 *  - territorySubtype is silently dropped (legacy leftover; never rendered in the modern listing).
 */
import { INITIAL_BUILDING_FILTERS, DEFAULT_ACTIVE_FILTER_KEYS, NON_SORTABLE_COLUMN_KEYS } from '../types';
function isLegacyCache(raw) {
    if (!raw || typeof raw !== 'object')
        return false;
    const obj = raw;
    if (!obj.filters || typeof obj.filters !== 'object')
        return false;
    const filters = obj.filters;
    const hasValidFilters = typeof filters.active === 'object' &&
        filters.active !== null &&
        typeof filters.values === 'object' &&
        filters.values !== null;
    if (!hasValidFilters)
        return false;
    if (obj.sort !== undefined && !isLegacySort(obj.sort))
        return false;
    return true;
}
function isLegacySort(value) {
    if (!Array.isArray(value))
        return false;
    if (value.length === 0)
        return true;
    const first = value[0];
    return (first !== null && typeof first === 'object' && typeof first.field === 'string' && typeof first.dir === 'string');
}
// ---------------------------------------------------------------------------
// Value converters
// ---------------------------------------------------------------------------
/** Convert an old multi-select value to a typed number array. */
function migrateNumberArray(raw) {
    if (raw == null || raw === '')
        return [];
    if (Array.isArray(raw))
        return raw.map(Number).filter((n) => !Number.isNaN(n));
    const n = Number(raw);
    return Number.isNaN(n) ? [] : [n];
}
/** Convert old boolean-select string ('true'/'false'/'') → boolean | null. */
function migrateBooleanSelect(raw) {
    if (raw === 'true' || raw === true)
        return true;
    if (raw === 'false' || raw === false)
        return false;
    return null;
}
/** Convert old text value → string. */
function migrateText(raw) {
    if (typeof raw === 'string')
        return raw;
    return '';
}
// ---------------------------------------------------------------------------
// Main migrator
// ---------------------------------------------------------------------------
export function createBuildingsLegacyMigrator(scope) {
    return (raw) => {
        var _a, _b;
        if (!isLegacyCache(raw))
            return null;
        const legacy = raw;
        const lv = legacy.filters.values;
        const la = legacy.filters.active;
        // ── Page size ──
        const pageSize = (_b = (_a = legacy.page) === null || _a === void 0 ? void 0 : _a.take) !== null && _b !== void 0 ? _b : 25;
        // ── Sort ──
        // Persisted sorts on non-sortable columns (siteId, inspectionsCount) must be dropped.
        let sort = null;
        if (legacy.sort && legacy.sort.length > 0) {
            const s = legacy.sort[0];
            if (!NON_SORTABLE_COLUMN_KEYS.has(s.field)) {
                sort = {
                    field: s.field,
                    direction: s.dir === 'asc' || s.dir === 'desc' ? s.dir : 'asc'
                };
            }
        }
        // ── Active filter keys (exclude territorySubtype — never in modern listing) ──
        const activeFilterKeys = Object.entries(la)
            .filter(([key, active]) => active === true && key !== 'territorySubtype')
            .map(([key]) => key);
        // ── Filter values ──
        const filters = Object.assign(Object.assign({}, INITIAL_BUILDING_FILTERS), { 
            // Multi-selects — clear account when in account scope (re-applied by adapter)
            account: scope.type === 'account' ? [] : migrateNumberArray(lv.account), tags: migrateNumberArray(lv.tags), territory: migrateNumberArray(lv.territory), additionalTerritory: migrateNumberArray(lv.additionalTerritory), salesPerson: migrateNumberArray(lv.salesPerson), compliancePlatform: migrateNumberArray(lv.compliancePlatform), 
            // Boolean select
            status: migrateBooleanSelect(lv.status), 
            // Text fields
            name: migrateText(lv.name), externalId: migrateText(lv.externalId) });
        // ── Visible columns (strip 'selected' pseudo-column) ──
        const visibleColumnKeys = Array.isArray(legacy.columns) ? legacy.columns.filter((c) => c !== 'selected') : [];
        return { pageSize, sort, filters, activeFilterKeys, visibleColumnKeys };
    };
}
export const buildingsLegacyMigrator = createBuildingsLegacyMigrator({
    type: 'standalone'
});
export { DEFAULT_ACTIVE_FILTER_KEYS };
